import { useCostMatrix } from '../api/hooks'
import type { CostBehavior, CostMatrixCell, CostTraceability } from '../api/types'

const TRACEABILITY: CostTraceability[] = ['Direct', 'Indirect']
const BEHAVIOR: CostBehavior[] = ['Fixed', 'Variable']

function money(value: number) {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * A run's allocated cost across the two axes of the cost matrix.
 *
 * Rendered as an actual 2×2 rather than a list of four rows. The whole reason for two
 * independent axes is that the interesting numbers are the margins — total fixed, total
 * indirect — and a table with row and column totals shows those without anybody adding
 * anything up.
 */
export function CostMatrixPanel({ runId }: { runId: number | null }) {
  const { data, isLoading } = useCostMatrix(runId)

  if (runId === null) {
    return null
  }

  if (isLoading || !data) {
    return (
      <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
        Loading cost matrix…
      </p>
    )
  }

  const cell = (t: CostTraceability | null, b: CostBehavior | null) =>
    data.totals.find((c: CostMatrixCell) => c.costTraceability === t && c.costBehavior === b)
      ?.allocatedAmount ?? 0

  const rowTotal = (t: CostTraceability) => BEHAVIOR.reduce((sum, b) => sum + cell(t, b), 0)
  const columnTotal = (b: CostBehavior) => TRACEABILITY.reduce((sum, t) => sum + cell(t, b), 0)

  const classified = TRACEABILITY.reduce((sum, t) => sum + rowTotal(t), 0)

  return (
    <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
      <div
        className="flex items-center gap-3 px-4 py-2"
        style={{ background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">cost matrix</h2>
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Direct cost is traceable to the department that consumes it; indirect is overhead
          spread across departments. Variable cost moves with volume, fixed does not.
        </span>
      </div>

      <table className="border-collapse" style={{ margin: '0.75rem 1rem' }}>
        <thead>
          <tr>
            <th className="col-head px-3 py-1.5 text-left" />
            {BEHAVIOR.map((b) => (
              <th key={b} className="col-head px-3 py-1.5 text-right">
                {b}
              </th>
            ))}
            <th
              className="col-head px-3 py-1.5 text-right"
              style={{ borderLeft: '1px solid var(--color-rule)' }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {TRACEABILITY.map((t) => (
            <tr key={t} className="border-t" style={{ borderColor: 'var(--color-rule-soft)' }}>
              <th className="col-head px-3 py-1.5 text-left">{t}</th>
              {BEHAVIOR.map((b) => (
                <td key={b} className="ident px-3 py-1.5 text-right">
                  {money(cell(t, b))}
                </td>
              ))}
              <td
                className="ident px-3 py-1.5 text-right"
                style={{
                  borderLeft: '1px solid var(--color-rule)',
                  color: 'var(--color-muted)',
                }}
              >
                {money(rowTotal(t))}
              </td>
            </tr>
          ))}

          <tr className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
            <th className="col-head px-3 py-1.5 text-left">Total</th>
            {BEHAVIOR.map((b) => (
              <td
                key={b}
                className="ident px-3 py-1.5 text-right"
                style={{ color: 'var(--color-muted)' }}
              >
                {money(columnTotal(b))}
              </td>
            ))}
            <td
              className="ident px-3 py-1.5 text-right"
              style={{ borderLeft: '1px solid var(--color-rule)', fontWeight: 600 }}
            >
              {money(classified)}
            </td>
          </tr>
        </tbody>
      </table>

      {/*
          The honesty line. While anything is unclassified the four cells do not add up to
          what the run posted, and a matrix that looks complete and is not is worse than no
          matrix — so the shortfall is stated rather than left to be noticed.
      */}
      {data.unclassifiedAmount !== 0 && (
        <p
          className="px-4 py-1.5 text-[12px]"
          style={{ background: 'var(--color-warn-wash)', color: 'var(--color-warn)' }}
        >
          <strong>{money(data.unclassifiedAmount)}</strong> of {money(data.totalAmount)} is in
          accounts nobody has classified, so it is not in the four cells above. Classify
          them on the chart of accounts to bring the matrix up to the run total.
        </p>
      )}
    </section>
  )
}
